
import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { X, Plus, ExternalLink, Edit, Save, Youtube, Video as VideoIcon } from 'lucide-react';
import { Video, VIDEO_SOURCES } from '@/types/video';
import { InputChangeHandler, TextAreaChangeHandler, ButtonClickHandler } from '@/types/events';

interface VideoManagerProps {
  videos: Video[];
  onVideosChange: (videos: Video[]) => void;
}

export const VideoManager = ({ videos, onVideosChange }: VideoManagerProps) => {
  const [isAddingNew, setIsAddingNew] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [newVideo, setNewVideo] = useState<Partial<Video>>({
    url: '',
    source: 'YouTube',
    title: '',
    description: '',
  });

  const getSourceIcon = (source: string) => {
    switch (source) {
      case 'YouTube':
        return <Youtube className="h-4 w-4 text-red-600" />;
      case 'Hudl':
        return <VideoIcon className="h-4 w-4 text-orange-600" />;
      case 'BallerTV':
        return <VideoIcon className="h-4 w-4 text-blue-600" />;
      default:
        return <VideoIcon className="h-4 w-4 text-gray-600" />;
    }
  };

  const addVideo = () => {
    if (!newVideo.url?.trim()) return;

    const video: Video = {
      id: `video-${Date.now()}`,
      url: newVideo.url,
      source: newVideo.source as Video['source'] || 'Other',
      title: newVideo.title || '',
      description: newVideo.description || '',
      created_at: new Date().toISOString(),
    };

    onVideosChange([...videos, video]);
    setNewVideo({ url: '', source: 'YouTube', title: '', description: '' });
    setIsAddingNew(false);
  };

  const updateVideo = (id: string, updates: Partial<Video>) => {
    const updatedVideos = videos.map(video =>
      video.id === id ? { ...video, ...updates } : video
    );
    onVideosChange(updatedVideos);
    setEditingId(null);
  };

  const removeVideo = (id: string) => {
    onVideosChange(videos.filter(video => video.id !== id));
  };

  const VideoForm = ({ 
    video, 
    onSave, 
    onCancel 
  }: { 
    video: Partial<Video>; 
    onSave: (video: Partial<Video>) => void; 
    onCancel: () => void; 
  }) => {
    const [formData, setFormData] = useState(video);

    return (
      <div className="space-y-4 p-4 border rounded-lg bg-gray-50">
        <div className="space-y-2">
          <Label htmlFor="video-url">Video URL *</Label>
          <Input
            id="video-url"
            value={formData.url || ''}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFormData({ ...formData, url: e.target.value })}
            placeholder="https://youtube.com/watch?v=... or https://hudl.com/..."
          />
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="video-source">Source *</Label>
            <Select 
              value={formData.source || 'YouTube'} 
              onValueChange={(value) => setFormData({ ...formData, source: value as Video['source'] })}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {VIDEO_SOURCES.map(source => (
                  <SelectItem key={source} value={source}>
                    <div className="flex items-center gap-2">
                      {getSourceIcon(source)}
                      {source}
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="video-title">Title (Optional)</Label>
            <Input
              id="video-title"
              value={formData.title || ''}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFormData({ ...formData, title: e.target.value })}
              placeholder="Video title"
            />
          </div>
        </div>

        <div className="space-y-2">
          <Label htmlFor="video-description">Description (Optional)</Label>
          <Textarea
            id="video-description"
            value={formData.description || ''}
            onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setFormData({ ...formData, description: e.target.value })}
            placeholder="Brief description of the video content"
            rows={2}
          />
        </div>

        <div className="flex gap-2">
          <Button type="button" onClick={() => onSave(formData)} size="sm">
            <Save className="h-4 w-4 mr-1" />
            Save
          </Button>
          <Button type="button" variant="outline" onClick={onCancel} size="sm">
            <X className="h-4 w-4 mr-1" />
            Cancel
          </Button>
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <Label className="text-base font-medium">Player Videos</Label>
        <Button 
          type="button" 
          onClick={() => setIsAddingNew(true)} 
          size="sm"
          disabled={isAddingNew}
        >
          <Plus className="h-4 w-4 mr-1" />
          Add Video
        </Button>
      </div>

      {isAddingNew && (
        <VideoForm
          video={newVideo}
          onSave={addVideo}
          onCancel={() => {
            setIsAddingNew(false);
            setNewVideo({ url: '', source: 'YouTube', title: '', description: '' });
          }}
        />
      )}

      {videos.length > 0 && (
        <div className="space-y-3">
          {videos.map((video) => (
            <Card key={video.id} className="border-l-4 border-l-blue-400">
              <CardHeader className="pb-3">
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-2">
                    {getSourceIcon(video.source)}
                    <CardTitle className="text-sm">
                      {video.title || `${video.source} Video`}
                    </CardTitle>
                  </div>
                  <div className="flex gap-1">
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => window.open(video.url, '_blank')}
                    >
                      <ExternalLink className="h-3 w-3" />
                    </Button>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => setEditingId(video.id)}
                    >
                      <Edit className="h-3 w-3" />
                    </Button>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => removeVideo(video.id)}
                    >
                      <X className="h-3 w-3" />
                    </Button>
                  </div>
                </div>
              </CardHeader>
              
              {editingId === video.id ? (
                <CardContent>
                  <VideoForm
                    video={video}
                    onSave={(updates) => updateVideo(video.id, updates)}
                    onCancel={() => setEditingId(null)}
                  />
                </CardContent>
              ) : (
                <CardContent className="pt-0">
                  <div className="space-y-2 text-sm text-gray-600">
                    <div className="truncate">
                      <strong>URL:</strong> {video.url}
                    </div>
                    {video.description && (
                      <div>
                        <strong>Description:</strong> {video.description}
                      </div>
                    )}
                    <div className="text-xs text-gray-400">
                      Added: {new Date(video.created_at).toLocaleDateString()}
                    </div>
                  </div>
                </CardContent>
              )}
            </Card>
          ))}
        </div>
      )}

      {videos.length === 0 && !isAddingNew && (
        <div className="text-center py-8 text-gray-500">
          <VideoIcon className="h-12 w-12 mx-auto mb-2 text-gray-300" />
          <p>No videos added yet</p>
          <p className="text-sm">Click "Add Video" to get started</p>
        </div>
      )}
    </div>
  );
};
